"use client"

import { useState } from "react"
import { MessageCircle } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { toast } from "@/components/ui/toast"
import { MESSAGES } from "@/components/ui/toast-messages"
import { submitFeedback, type FeedbackTarget } from "@/lib/submit_feedback"
import { useTranslations } from "next-intl"

type ButtonProps = React.ComponentProps<typeof Button>

interface FeedbackDialogProps {
  /** Styling for the trigger button, so a caller can match its surroundings —
   *  the paragraph action pill uses a smaller, borderless variant. Defaults
   *  preserve the standalone look used everywhere else. */
  triggerVariant?: ButtonProps["variant"]
  triggerSize?: ButtonProps["size"]
  /** Side the trigger's tooltip opens on. */
  tooltipSide?: "top" | "bottom" | "left" | "right"
  /**
   * What the feedback is about, when it is about one thing — a proposition — so it is stored against it.
   * Without one, it is feedback on the app.
   */
  target?: { type: FeedbackTarget; id: string | null }
}

/**
 * "Tell us what to fix."
 *
 * The dialog now uses the Dialog primitives rather than a layout of its own. It had a bare
 * DialogTitle, a plain `<div>` where DialogDescription goes, and a hand-built right-aligned row
 * where DialogFooter goes — so it inherited none of the shell's typography (the title, the
 * 24px header rhythm) and instead set its own. Every one of those pieces already exists in
 * components/ui/dialog and is what the rest of the app's dialogs get for free.
 *
 * The two actions were also icon-only circles: an unlabelled ✕ next to an unlabelled ➤, which is
 * the one place in the app where a destructive-looking and a confirming action sat side by side
 * with nothing but glyphs to tell them apart. They are ordinary labelled buttons now.
 */
function FeedbackDialog({
  triggerVariant = "secondary",
  triggerSize = "icon",
  tooltipSide = "bottom",
  target,
}: FeedbackDialogProps = {}) {
  const t = useTranslations("feedback")
  const [text, setText] = useState("")
  const [open, setOpen] = useState(false)
  const [sending, setSending] = useState(false)

  const handleSubmit = async () => {
    const trimmed = text.trim()
    if (!trimmed) {
      toast(MESSAGES.feedback.empty)
      return
    }

    /*
      Awaited, and the box is emptied only on success.

      It used to call submitFeedback without awaiting it and clear the text and close in the same
      tick — so a rejected send took the reader's words with it, and the failure toast arrived
      over a dialog that had already gone. Whatever they wrote survives a failure now, and the
      dialog stays open on the text so they can try again.
    */
    setSending(true)
    const result = await submitFeedback({
      targetType: target?.type ?? "app_ux",
      targetId: target?.id ?? null,
      text: trimmed,
    })
    setSending(false)

    if (result.success) {
      setText("")
      setOpen(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant={triggerVariant}
            size={triggerSize}
            aria-label={t("open")}
            onClick={() => setOpen(true)}
          >
            {/* No size class: Button sizes its own icons, and the header's other controls take
                that default. Setting `h-4 w-4` here opted this one button out of it. */}
            <MessageCircle />
          </Button>
        </TooltipTrigger>
        <TooltipContent side={tooltipSide}>{t("open")}</TooltipContent>
      </Tooltip>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>
            {t("haveAnIdea")}
          </DialogDescription>
        </DialogHeader>

        <Textarea
          id="feedback"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("placeholder")}
          disabled={sending}
          className="min-h-32"
        />

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)} disabled={sending}>
            {t("cancel")}
          </Button>
          {/* Disabled on an empty box as well as mid-send: the emptiness is visible, so a button
              that looks ready and then answers with an error toast is telling you something you
              could already see. */}
          <Button onClick={handleSubmit} disabled={sending || !text.trim()}>
            {sending ? t("sending") : t("send")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export { FeedbackDialog }
